import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link, useNavigate, useParams} from "react-router-dom";
import {
    deleteCodeTask,
    exportCodeTasksByLesson,
    getCodeTaskDetails,
    getCodeTasksList,
    importCodeTasksByLesson
} from "../api/api";
import {CodeTaskDetailsResponse, CodeTaskListResponse} from "../types";
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Container,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Typography,
} from "@mui/material";
import {Cancel, CheckCircle, HelpOutline} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {useAlert} from "../context/AlertContext";
import AddCodeTaskModal from "../components/AddCodeTaskModal";
import EditCodeTaskModal from "../components/EditCodeTaskModal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import BreadcrumbLink from "../components/BreadcrumbLink";
import PeopleIcon from "@mui/icons-material/People";
import Tooltip from "@mui/material/Tooltip";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ImportCodeTasksPreviewModal, {ImportPreviewTask} from "../components/ImportCodeTasksPreviewModal";

const TasksPage = () => {
    const {t} = useTranslation();

    const {lessonId} = useParams<{ lessonId?: string }>();
    const safeLessonId = lessonId ?? "";
    const navigate = useNavigate();

    const [data, setData] = useState<CodeTaskListResponse | null>(null);
    const {showAlert} = useAlert();
    const [loading, setLoading] = useState(false);

    const [addOpen, setAddOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState<any | null>(null);
    const [editLoading, setEditLoading] = useState(false);

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTaskId, setDeleteTaskId] = useState<number | null>(null);

    const [importing, setImporting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [selectedImportFile, setSelectedImportFile] = useState<File | null>(null);
    const [importPreviewOpen, setImportPreviewOpen] = useState(false);
    const [importPreviewTasks, setImportPreviewTasks] = useState<ImportPreviewTask[]>([]);

    const load = async () => {
        if (!safeLessonId) return;

        try {
            setLoading(true);
            const res = await getCodeTasksList(Number(safeLessonId));
            setData(res);
        } catch (error: any) {
            showAlert(error?.message ?? t("codetaskslist.errors.loadFailed"), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeLessonId]);

    const openEdit = async (task: any) => {
        try {
            setEditLoading(true);

            const details: CodeTaskDetailsResponse = await getCodeTaskDetails(task.id);

            setSelectedTask({
                id: details.id,
                title: details.title,
                content: details.content,
                testCases: details.testCases,
                language: details.language,
                orderNum: details.orderNum,
                starterCode: details.starterCode
            });

            setEditOpen(true);
        } catch (e: any) {
            showAlert(e?.message ?? t("codetaskslist.errors.loadDetailsFailed"), "error");
        } finally {
            setEditLoading(false);
        }
    };

    const closeEdit = () => {
        setEditOpen(false);
        setSelectedTask(null);
    };

    const openDelete = (taskId: number) => {
        setDeleteTaskId(taskId);
        setDeleteOpen(true);
    };

    const closeDelete = () => {
        setDeleteOpen(false);
        setDeleteTaskId(null);
    };

    const confirmDelete = async () => {
        if (!deleteTaskId) return;

        try {
            await deleteCodeTask(deleteTaskId);
            showAlert(t("codetaskslist.alerts.deleted"), "success");
            closeDelete();
            await load();
        } catch (e: any) {
            showAlert(e?.message ?? t("codetaskslist.errors.deleteFailed"), "error");
        }
    };

    const openImportFilePicker = () => {
        fileInputRef.current?.click();
    };

    const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = "";

        if (!file || !safeLessonId) return;

        if (!file.name.toLowerCase().endsWith(".json")) {
            showAlert(t("codetaskslist.errors.selectJsonFile"), "error");
            return;
        }

        try {
            const text = await file.text();
            const parsed = JSON.parse(text);

            if (!parsed || !Array.isArray(parsed.tasks)) {
                showAlert(t("codetaskslist.errors.invalidImportFile"), "error");
                return;
            }

            if (parsed.tasks.length === 0) {
                showAlert(t("codetaskslist.errors.emptyImportFile"), "error");
                return;
            }

            const previewTasks: ImportPreviewTask[] = parsed.tasks.map((task: any, index: number) => {
                const title =
                    typeof task.title === "string" && task.title.trim()
                        ? task.title.trim()
                        : t("codetaskslist.taskWithoutTitle", {number: index + 1});

                const language =
                    typeof task.language === "string" && task.language.trim()
                        ? task.language.trim()
                        : undefined;

                const testCasesCount = Array.isArray(task.testCases)
                    ? task.testCases.length
                    : 0;

                return {
                    title,
                    language,
                    testCasesCount,
                };
            });

            setSelectedImportFile(file);
            setImportPreviewTasks(previewTasks);
            setImportPreviewOpen(true);
        } catch {
            showAlert(t("codetaskslist.errors.invalidJsonFile"), "error");
        }
    };

    const closeImportPreview = () => {
        if (importing) return;

        setImportPreviewOpen(false);
        setSelectedImportFile(null);
        setImportPreviewTasks([]);
    };

    const confirmImport = async () => {
        if (!selectedImportFile || !safeLessonId) return;

        try {
            setImporting(true);

            const result = await importCodeTasksByLesson(
                Number(safeLessonId),
                selectedImportFile
            );

            showAlert(
                t("codetaskslist.alerts.imported", {count: result.importedTasks}),
                "success"
            );

            setImportPreviewOpen(false);
            setSelectedImportFile(null);
            setImportPreviewTasks([]);

            await load();
        } catch (e: any) {
            showAlert(e?.message ?? t("codetaskslist.errors.importFailed"), "error");
        } finally {
            setImporting(false);
        }
    };

    const pad = (value: number) => String(value).padStart(2, "0");

    const getDateTimeFilePrefix = () => {
        const now = new Date();

        const year = now.getFullYear();
        const month = pad(now.getMonth() + 1);
        const day = pad(now.getDate());
        const hour = pad(now.getHours());
        const minute = pad(now.getMinutes());

        return `${year}_${month}_${day}_${hour}_${minute}`;
    };

    const sanitizeFileName = (value: string) => {
        return value
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9а-яёәіңғүұқөһ-]+/gi, "_")
            .replace(/_+/g, "_")
            .replace(/^_|_$/g, "");
    };

    const handleExport = async () => {
        if (!safeLessonId) return;

        try {
            setExporting(true);

            const blob = await exportCodeTasksByLesson(Number(safeLessonId));

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            const lessonTitle = data?.lessonTitle
                ? sanitizeFileName(data.lessonTitle)
                : `lesson_${safeLessonId}`;

            const dateTimePrefix = getDateTimeFilePrefix();

            link.href = url;
            link.download = `${dateTimePrefix}_${lessonTitle}_code_tasks.json`;

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);

            showAlert(t("codetaskslist.alerts.exported"), "success");
        } catch (e: any) {
            showAlert(e?.message ?? t("codetaskslist.errors.exportFailed"), "error");
        } finally {
            setExporting(false);
        }
    };

    const circleButtonSx = (theme: any) => ({
        width: 44,
        height: 44,
        borderRadius: "50%",
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
        "&:hover": {
            backgroundColor: theme.palette.action.selected,
        },
        "&.Mui-disabled": {
            backgroundColor: theme.palette.action.disabledBackground,
        },
    });

    if (loading && !data) {
        return (
            <Container sx={{mt: 6, display: "flex", justifyContent: "center"}}>
                <CircularProgress/>
            </Container>
        );
    }

    if (!data) {
        return (
            <Container sx={{mt: 4}}>
                <Button component={Link} to="/" variant="outlined">
                    {t("common.toMainPage")}
                </Button>
            </Container>
        );
    }

    return (
        <Box sx={{px: {xs: 3, md: 3}}}>
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small"/>}
                maxItems={3}
                itemsBeforeCollapse={1}
                itemsAfterCollapse={2}
                sx={{
                    mb: 2,
                    "& .MuiBreadcrumbs-ol": {
                        overflow: "hidden",
                    },
                    "& .MuiBreadcrumbs-li": {
                        minWidth: 0,
                    },
                }}
            >
                <BreadcrumbLink to="/courses">
                    {t("nav.courses")}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/course/${data.courseId}`}>
                    {data.courseTitle}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/chapter/${data.chapterId}`}>
                    {data.chapterTitle}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/lesson/${data.lessonId}`}>
                    {data.lessonTitle}
                </BreadcrumbLink>

                <Typography color="text.primary" variant="body2">
                    <strong>{t("codetaskslist.tasks")}</strong>
                </Typography>
            </Breadcrumbs>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography sx={{fontSize: 28, fontWeight: 800}} noWrap>
                    {data.lessonTitle}
                </Typography>

                <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 2}}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".json,application/json"
                        style={{display: "none"}}
                        onChange={handleImportFile}
                    />

                    <Tooltip title={t("codetaskslist.importTooltip")}>
                        <span>
                            <IconButton
                                onClick={openImportFilePicker}
                                disabled={importing || exporting}
                                aria-label={t("codetaskslist.importTooltip")}
                                sx={circleButtonSx}
                            >
                                {importing ? <CircularProgress size={22}/> : <UploadFileIcon/>}
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title={t("codetaskslist.exportTooltip")}>
                        <span>
                            <IconButton
                                onClick={handleExport}
                                disabled={importing || exporting || data.tasks.length === 0}
                                aria-label={t("codetaskslist.exportTooltip")}
                                sx={circleButtonSx}
                            >
                                {exporting ? <CircularProgress size={22}/> : <FileDownloadIcon/>}
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title={t("codetaskslist.addTooltip")}>
                        <span>
                            <IconButton
                                onClick={() => setAddOpen(true)}
                                disabled={importing || exporting}
                                aria-label={t("codetaskslist.addTooltip")}
                                sx={circleButtonSx}
                            >
                                <AddIcon/>
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {data.tasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {t("codetaskslist.noCodeTasks")}
                </Typography>
            ) : (
                <Box
                    sx={(theme) => ({
                        mt: 3,
                        mb: 2,
                        border: `1px solid ${theme.palette.divider}`,
                    })}
                >
                    <List sx={{p: 0}}>
                        {data.tasks
                            .slice()
                            .sort((a, b) => a.orderNum - b.orderNum)
                            .map((task) => (
                                <ListItem
                                    key={task.id}
                                    sx={(theme) => ({
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        px: 2,
                                        py: 1.5,
                                        display: "block",
                                        "&:last-child": {
                                            borderBottom: "none",
                                        },
                                    })}
                                >
                                    <Box
                                        sx={{
                                            width: "100%",
                                            display: "flex",
                                            alignItems: {xs: "stretch", sm: "center"},
                                            justifyContent: "space-between",
                                            gap: 2,
                                            flexDirection: {xs: "column", sm: "row"},
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                minWidth: 0,
                                                flex: 1,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.2,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {task.solved === true && (
                                                    <CheckCircle fontSize="small" color="success"/>
                                                )}

                                                {task.solved === false && (
                                                    <Cancel fontSize="small" color="error"/>
                                                )}

                                                {task.solved === null && (
                                                    <HelpOutline fontSize="small" color="disabled"/>
                                                )}
                                            </Box>

                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    color: "text.primary",
                                                    minWidth: 0,
                                                    wordBreak: "break-word",
                                                }}
                                            >
                                                {task.orderNum}. {task.title}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                flexShrink: 0,
                                                justifyContent: {xs: "flex-end", sm: "flex-start"},
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                sx={{
                                                    textTransform: "none",
                                                    minWidth: 96,
                                                    px: 1.5,
                                                    whiteSpace: "nowrap",
                                                }}
                                                onClick={() => navigate(`/code-tasks/${task.id}`)}
                                            >
                                                {t("codetaskslist.details")}
                                            </Button>

                                            <Tooltip title={t("codetaskslist.attempts")}>
                                                <IconButton
                                                    onClick={() => navigate(`/admin/code-tasks/${task.id}/attempts`)}
                                                    aria-label={t("codetaskslist.attempts")}
                                                    size="small"
                                                >
                                                    <PeopleIcon/>
                                                </IconButton>
                                            </Tooltip>

                                            <IconButton
                                                onClick={() => openEdit(task)}
                                                aria-label={t("common.edit")}
                                                size="small"
                                                disabled={editLoading}
                                            >
                                                <EditIcon/>
                                            </IconButton>

                                            <IconButton
                                                onClick={() => openDelete(task.id)}
                                                aria-label={t("common.delete")}
                                                size="small"
                                            >
                                                <DeleteIcon/>
                                            </IconButton>
                                        </Box>
                                    </Box>
                                </ListItem>
                            ))}
                    </List>
                </Box>
            )}

            <ImportCodeTasksPreviewModal
                open={importPreviewOpen}
                fileName={selectedImportFile?.name ?? ""}
                tasks={importPreviewTasks}
                importing={importing}
                onClose={closeImportPreview}
                onConfirm={confirmImport}
            />

            <AddCodeTaskModal
                open={addOpen}
                onClose={() => setAddOpen(false)}
                lessonId={Number(safeLessonId)}
                onCreated={load}
            />

            <EditCodeTaskModal
                open={editOpen}
                onClose={closeEdit}
                task={selectedTask}
                onUpdated={load}
            />

            <DeleteConfirmationModal
                open={deleteOpen}
                onClose={closeDelete}
                onConfirm={confirmDelete}
            />
        </Box>
    );
};

export default TasksPage;